// EInvoice.jsx
import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { DataContext } from "../../context/DataContext";
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  IconButton,
  Paper,
  Grid,
  alpha,
  Drawer,
  Alert,
  useTheme,
  Chip,
  InputAdornment,
  CircularProgress,
  Backdrop,
  Tooltip,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Autocomplete
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Close as CloseIcon,
  Print as PrintIcon,
  Delete as DeleteIcon,
  Person,
  Receipt,
  Inventory,
  Visibility as PreviewIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import { AgGridReact } from "ag-grid-react";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parse } from "date-fns";

ModuleRegistry.registerModules([AllCommunityModule]);

const STATE_LIST = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
];

// ================================================================
// HELPERS
// ================================================================
const roundTo3 = (n) => Math.round((parseFloat(n) || 0) * 1000) / 1000;
const roundTo2 = (n) => Math.round((parseFloat(n) || 0) * 100) / 100;

const formatDateDisplay = (dateStr) => {
  if (!dateStr) return "";
  try {
    if (dateStr.includes("-")) {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return format(d, "dd/MM/yyyy");
    }
    return dateStr;
  } catch {
    return dateStr;
  }
};

const numberToWords = (num) => {
  if (typeof num !== "number") num = parseFloat(num) || 0;
  num = Math.floor(num);
  if (num === 0) return "Zero Rupees Only";
  const ones = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight",
    "Nine", "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen",
    "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const conv = (n) => {
    if (n === 0) return "";
    let r = "";
    if (n >= 100) { r += ones[Math.floor(n / 100)] + " Hundred "; n %= 100; }
    if (n >= 20) { r += tens[Math.floor(n / 10)] + " "; n %= 10; }
    if (n > 0) r += ones[n] + " ";
    return r.trim();
  };
  const toIndian = (n) => {
    if (n === 0) return "";
    let crore = Math.floor(n / 10000000); n %= 10000000;
    let lakh = Math.floor(n / 100000); n %= 100000;
    let thousand = Math.floor(n / 1000); n %= 1000;
    let hundred = n;
    let r = "";
    if (crore > 0) r += conv(crore) + " Crore ";
    if (lakh > 0) r += conv(lakh) + " Lakh ";
    if (thousand > 0) r += conv(thousand) + " Thousand ";
    if (hundred > 0) r += conv(hundred);
    return r.trim();
  };
  const w = toIndian(num);
  return w ? w + " Rupees Only" : "Zero Rupees Only";
};

const getBillStyles = () => `
@media print {
  body { margin: 0 !important; padding: 0 !important; background: white !important; }
  body * { visibility: hidden !important; }
  #einvoiceBillContent { position: absolute !important; left: 0 !important; top: 0 !important;
    width: 100% !important; max-width: 100% !important; margin: 0 !important;
    padding: 6mm 6mm !important; box-shadow: none !important; background: white !important;
    visibility: visible !important; border: none !important; }
  #einvoiceBillContent * { visibility: visible !important; }
  table { page-break-inside: avoid !important; }
  tr { page-break-inside: avoid !important; }
  @page { size: A4 portrait; margin: 6mm; }
  [style*="background-color"], [style*="background:"] {
    -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
  }
}
body { font-family: Arial, Helvetica, sans-serif; background: #f5f5f5; margin: 0; padding: 20px; }
#einvoiceBillContent { max-width: 820px; width: 100%; margin: 0 auto; background: white;
  padding: 12px 15px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  box-sizing: border-box; font-size: 12px; }
`;

// ================================================================
// LOADING SKELETON
// ================================================================
const EInvoiceSkeleton = ({ isDark }) => {
  const skeletonBg = isDark
    ? "linear-gradient(90deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.12) 50%, rgba(255,255,255,0.05) 100%)"
    : "linear-gradient(90deg, #eef2f7 0%, #f8fafc 50%, #eef2f7 100%)";

  const sk = (props = {}) => (
    <Skeleton
      variant="rounded"
      animation="wave"
      sx={{
        bgcolor: "transparent",
        background: skeletonBg,
        borderRadius: 1,
        ...props.sx,
      }}
      width={props.width}
      height={props.height}
    />
  );

  return (
    <Box sx={{ minHeight: "100vh", py: 2 }}>
      <Container maxWidth="xl">
        {/* ---------- Header skeleton ---------- */}
        <Card
          sx={{
            mb: 3,
            background: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,0.6)",
            borderRadius: 2,
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ alignItems: "center" }}
            >
              <Box sx={{ mb: 1, minWidth: 180 }}>
                {sk({ width: 160, height: 36, sx: { mb: 1 } })}
                {sk({ width: 140, height: 22 })}
              </Box>

              <Box sx={{ flex: 1, minWidth: 250 }}>
                {sk({ height: 40, sx: { borderRadius: 2 } })}
              </Box>

              {sk({ width: 110, height: 36, sx: { borderRadius: 2 } })}
              {sk({ width: 150, height: 36, sx: { borderRadius: 2 } })}
            </Stack>
          </CardContent>
        </Card>

        {/* ---------- Grid card skeleton ---------- */}
        <Card
          elevation={0}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            height: "calc(100vh - 400px)",
            minHeight: 500,
            background: isDark
              ? "linear-gradient(180deg, #1a1a2e 0%, #16161a 100%)"
              : "linear-gradient(180deg, #ffffff 0%, rgba(248, 250, 252, 0.1) 100%)",
            border: isDark
              ? "1px solid rgba(255, 255, 255, 0.06)"
              : "1px solid rgba(0, 0, 0, 0.04)",
            boxShadow: isDark
              ? "0 4px 24px rgba(0, 0, 0, 0.4)"
              : "0 4px 24px rgba(0, 0, 0, 0.06)",
          }}
        >
          {/* Header row of the grid */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "60px 120px 200px 180px 140px 130px 90px 160px 100px",
              gap: 0,
              px: 2,
              py: 2,
              borderBottom: isDark
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.06)",
              alignItems: "center",
            }}
          >
            {["#", "BILL NO", "CUSTOMER", "GST NO", "STATE", "DATE", "ITEMS", "GRAND TOTAL", "ACTIONS"].map(
              (h) => (
                <Box key={h}>
                  {sk({ width: "70%", height: 14 })}
                </Box>
              )
            )}
          </Box>

          {/* Body rows */}
          <Box sx={{ p: 0 }}>
            {Array.from({ length: 8 }).map((_, rowIdx) => (
              <Box
                key={rowIdx}
                sx={{
                  display: "grid",
                  gridTemplateColumns: "60px 120px 200px 180px 140px 130px 90px 160px 100px",
                  alignItems: "center",
                  px: 2,
                  py: 2.5,
                  borderBottom: isDark
                    ? "1px solid rgba(255,255,255,0.04)"
                    : "1px solid rgba(0,0,0,0.03)",
                  // background:
                  //   rowIdx % 2 === 0
                  //     ? "transparent"
                  //     : isDark
                  //       ? "rgba(255,255,255,0.01)"
                  //       : "rgba(248, 250, 252, 0.1)",
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {sk({ width: 18, height: 18, sx: { borderRadius: "50%" } })}
                </Box>
                {sk({ width: "70%", height: 16 })}
                {sk({ width: "85%", height: 16 })}
                {sk({ width: "75%", height: 16 })}
                {sk({ width: "60%", height: 16 })}
                {sk({ width: "55%", height: 16 })}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {sk({ width: 24, height: 24, sx: { borderRadius: "50%" } })}
                </Box>
                {sk({ width: "70%", height: 16 })}
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {sk({ width: 32, height: 32, sx: { borderRadius: "50%" } })}
                </Box>
              </Box>
            ))}
          </Box>
        </Card>
      </Container>
    </Box>
  );
};


// ================================================================
// ACTIONS RENDERER (Print button per grid row)
// ================================================================
const ActionsRenderer = (props) => {
  return (
    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center", width: "100%" }}>
      <Tooltip title="Print Bill" arrow>
        <IconButton
          size="small"
          onClick={() => props.context.handlePrint(props.data)}
          sx={{
            color: "#2563EB",
            "&:hover": {
              backgroundColor: "rgba(37, 99, 235, 0.1)",
              transform: "scale(1.1)",
            },
            transition: "all 0.15s ease",
          }}
        >
          <PrintIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

const ACCENT = "#2563EB";

// Section = title row + content. Flat, divided, no nested card chrome.
const Section = ({ icon, title, action, isDark, children }) => (
  <Box sx={{ mb: 3 }}>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        mb: 1.5,
        pb: 1,
        borderBottom: `1px solid ${(isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)")}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {icon}
        <Typography variant="subtitle2" sx={{ fontWeight: 700, letterSpacing: 0.2 }}>
          {title}
        </Typography>
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

// ================================================================
// MAIN COMPONENT
// ================================================================
const EInvoice = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // NOTE: DataContext exposes `eInvoices` (from data.EInvoices)
  const {
    eInvoices = [],
    addEInvoice,
    loading: contextLoading,
    refresh,
    customers: customerData
  } = useContext(DataContext);

  // ================================================================
  // AG GRID THEME STYLES — matches the Customers page look
  // ================================================================
  const getGridSx = (isDark) => ({
    width: "100%",
    height: "100%",
    "& .ag-root-wrapper": {
      border: "none !important",
      borderRadius: "20px",
    },
    "& .ag-header": {
      background: isDark
        ? "linear-gradient(180deg, #252536 0%, #1e1e2d 100%) !important"
        : "linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%) !important",
      borderBottom: isDark
        ? "1px solid rgba(255, 255, 255, 0.08) !important"
        : "1px solid rgba(0, 0, 0, 0.06) !important",
    },
    "& .ag-header-cell": {
      fontWeight: "700 !important",
      fontSize: "12px !important",
      textTransform: "uppercase !important",
      letterSpacing: "0.8px !important",
      color: isDark ? "#94a3b8 !important" : "#64748b !important",
      borderRight: "none !important",
      transition: "background 0.2s ease !important",
      "&:hover": {
        background: isDark
          ? "rgba(37, 99, 235, 0.1) !important"
          : "rgba(37, 99, 235, 0.05) !important",
      },
    },
    "& .ag-body-viewport": {
      background: isDark ? "#16161a !important" : "#ffffff !important",
    },
    "& .ag-row": {
      borderBottom: isDark
        ? "1px solid rgba(255, 255, 255, 0.04) !important"
        : "1px solid rgba(0, 0, 0, 0.03) !important",
      transition: "background 0.15s ease !important",
      "&:hover": {
        background: isDark
          ? "linear-gradient(90deg, rgba(37, 99, 235, 0.08), rgba(139, 92, 246, 0.05)) !important"
          : "linear-gradient(90deg, rgba(37, 99, 235, 0.04), rgba(139, 92, 246, 0.02)) !important",
      },
    },
    "& .ag-row-odd": {
      background: isDark
        ? "rgba(255, 255, 255, 0.01) !important"
        : "rgba(248, 250, 252, 0.5) !important",
    },
    "& .ag-row-even": {
      background: isDark ? "transparent !important" : "#ffffff !important",
    },
    "& .ag-cell": {
      display: "flex !important",
      alignItems: "center !important",
      color: isDark ? "#e2e8f0 !important" : "#1e293b !important",
      fontSize: "14px !important",
      fontWeight: "500 !important",
      borderRight: "none !important",
      padding: "0 16px !important",
    },
    "& .ag-pinned-left-cols-container .ag-cell, & .ag-pinned-right-cols-container .ag-cell": {
      background: isDark ? "#16161a !important" : "#ffffff !important",
    },
  });

  const gridRef = useRef();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");
  const [isSaving, setIsSaving] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [printData, setPrintData] = useState(null);

  const getEmptyItem = (id) => ({
    id,
    itemName: "92.5 SILVER ORNAMENTS",
    hsn: "711311",
    grossWeight: "",
    netWeight: "",
    rate: "",
    per: "grm",
    amount: 0,
  });

  const [formData, setFormData] = useState({
    billNo: "",
    date: new Date().toISOString().split("T")[0],
    customerId: "",
    customerName: "",
    address: "",
    gstNo: "",
    state: "Gujarat",
    itemDetails: [getEmptyItem(1)],
    taxType: "cgst",
    roundOffDirection: "add",
    entryMode: "weight",
    gstTotalInput: "",
    taxableAmount: 0,
    cgst: 0,
    sgst: 0,
    igst: 0,
    roundOff: 0,
    roundOffManual: null,
    grandTotal: 0,
    amountInWords: "",
  });

  // -------- Bill number generation --------
  const generateBillNumber = useCallback(() => {
    if (!eInvoices || eInvoices.length === 0) return "1001";

    const sorted = [...eInvoices].sort((a, b) => {
      const na = parseInt(String(a.billNo ?? "").replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(String(b.billNo ?? "").replace(/\D/g, ""), 10) || 0;
      return nb - na;
    });

    const latest = sorted[0];
    if (latest?.billNo != null && latest.billNo !== "") {
      const digits = String(latest.billNo).replace(/\D/g, "");
      const num = parseInt(digits, 10) || 1000;
      return String(num + 1);
    }
    return "1001";
  }, [eInvoices]);

  const getLastBillNumber = useCallback(() => {
    if (!eInvoices || eInvoices.length === 0) return "—";

    const sorted = [...eInvoices].sort((a, b) => {
      const na = parseInt(String(a.billNo ?? "").replace(/\D/g, ""), 10) || 0;
      const nb = parseInt(String(b.billNo ?? "").replace(/\D/g, ""), 10) || 0;
      return nb - na;
    });

    return sorted[0]?.billNo != null ? String(sorted[0].billNo) : "—";
  }, [eInvoices]);

  useEffect(() => {
    if (!formData.billNo) {
      setFormData((p) => ({ ...p, billNo: generateBillNumber() }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eInvoices]);

  // Given a GST-inclusive grand total, back-calculate taxable, tax, roundOff, and metal rate.
  const recalcFromGstTotal = useCallback(
    (gstTotal, taxType, roundOffDirection, items) => {
      const total = roundTo2(gstTotal);
      if (!total || total <= 0) {
        return {
          taxableAmount: 0, cgst: 0, sgst: 0, igst: 0,
          roundOff: 0, grandTotal: 0, amountInWords: numberToWords(0),
          itemDetails: items,
        };
      }

      const taxRate = taxType === "cgst" ? 0.03 : 0.03; // both are 3% total
      const taxable = roundTo2(total / (1 + taxRate));

      let cgst = 0, sgst = 0, igst = 0;
      if (taxType === "cgst") {
        cgst = roundTo2(taxable * 0.015);
        sgst = roundTo2(taxable * 0.015);
      } else {
        igst = roundTo2(taxable * 0.03);
      }

      const beforeRound = roundTo2(taxable + cgst + sgst + igst);
      let roundOff = roundTo2(total - beforeRound);
      if (roundOffDirection === "subtract") roundOff = -Math.abs(roundOff);
      else roundOff = Math.abs(roundOff);

      // Distribute taxable across items proportional to their gross weight
      // (single-item case: all on that item; multi-item: pro-rata by netWeight).
      const totalWeight = items.reduce(
        (s, it) => s + (parseFloat(it.netWeight) || 0), 0
      );

      const newItems = items.map((it) => {
        const nw = parseFloat(it.netWeight) || 0;
        let share = 0;
        if (totalWeight > 0) share = taxable * (nw / totalWeight);
        else share = taxable / items.length;

        const amount = roundTo2(share);
        const rate = nw > 0 ? roundTo2(amount / nw) : 0;
        return { ...it, amount, rate };
      });

      // Rebalance last item to absorb rounding drift so taxable matches exactly
      const sumAmounts = newItems.reduce((s, it) => s + (it.amount || 0), 0);
      const drift = roundTo2(taxable - sumAmounts);
      if (Math.abs(drift) >= 0.01 && newItems.length > 0) {
        const last = newItems[newItems.length - 1];
        const newAmount = roundTo2((last.amount || 0) + drift);
        const nw = parseFloat(last.netWeight) || 0;
        newItems[newItems.length - 1] = {
          ...last,
          amount: newAmount,
          rate: nw > 0 ? roundTo2(newAmount / nw) : last.rate,
        };
      }

      return {
        taxableAmount: taxable,
        cgst, sgst, igst,
        roundOff,
        grandTotal: total,
        amountInWords: numberToWords(total),
        itemDetails: newItems,
      };
    },
    []
  );

  // -------- Recalc totals --------
  const recalcTotals = useCallback(
    (items, taxType, roundOffDirection, roundOffManual = null) => {
      const taxable = roundTo2(
        items.reduce((sum, it) => sum + (parseFloat(it.amount) || 0), 0)
      );

      let cgst = 0, sgst = 0, igst = 0;
      if (taxType === "cgst") {
        cgst = roundTo2(taxable * 0.015);
        sgst = roundTo2(taxable * 0.015);
      } else {
        igst = roundTo2(taxable * 0.03);
      }

      const beforeRound = roundTo2(taxable + cgst + sgst + igst);

      let roundOff;
      if (roundOffManual !== null && roundOffManual !== "" && !isNaN(parseFloat(roundOffManual))) {
        // user typed an explicit value — keep its sign
        roundOff = roundTo2(roundOffManual);
      } else {
        const nearest = Math.round(beforeRound);
        roundOff = roundTo2(nearest - beforeRound);
        if (roundOffDirection === "subtract") roundOff = -Math.abs(roundOff);
        else roundOff = Math.abs(roundOff);
      }

      const grandTotal = roundTo2(beforeRound + roundOff);

      return {
        taxableAmount: taxable,
        cgst,
        sgst,
        igst,
        roundOff,
        grandTotal,
        amountInWords: numberToWords(grandTotal),
      };
    },
    []
  );

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => {
      // --- Entry mode toggle ---
      if (name === "entryMode") {
        if (value === "gstAmount") {
          // keep whatever taxable / items exist; just switch the input UI
          return { ...prev, entryMode: value };
        }
        // switching back to weight mode: clear manual gst input
        return { ...prev, entryMode: value, gstTotalInput: "" };
      }

      // --- User types a GST-inclusive total ---
      if (name === "gstTotalInput") {
        const num = parseFloat(value) || 0;
        const next = { ...prev, gstTotalInput: value };
        if (num > 0) {
          Object.assign(
            next,
            recalcFromGstTotal(
              num,
              prev.taxType,
              prev.roundOffDirection,
              prev.itemDetails
            )
          );
          next.roundOffManual = null; // auto in this mode
        } else {
          // cleared → wipe monetary fields
          next.taxableAmount = 0;
          next.cgst = 0; next.sgst = 0; next.igst = 0;
          next.roundOff = 0;
          next.grandTotal = 0;
          next.amountInWords = "";
        }
        return next;
      }

      // --- Manual round off override ---
      if (name === "roundOffManual") {
        const raw = value;
        const parsed =
          raw === "" || raw === "-" ? null : parseFloat(raw);
        const next = {
          ...prev,
          roundOffManual:
            parsed === null || isNaN(parsed) ? null : parsed,
        };
        // manual round off is only meaningful in weight mode
        Object.assign(
          next,
          recalcTotals(
            prev.itemDetails,
            prev.taxType,
            prev.roundOffDirection,
            parsed
          )
        );
        return next;
      }

      // --- Tax type / round-off-direction change ---
      if (name === "taxType" || name === "roundOffDirection") {
        const next = { ...prev, [name]: value };
        const newTaxType = name === "taxType" ? value : prev.taxType;
        const newDir =
          name === "roundOffDirection" ? value : prev.roundOffDirection;

        if (prev.entryMode === "gstAmount" && parseFloat(prev.gstTotalInput) > 0) {
          Object.assign(
            next,
            recalcFromGstTotal(
              parseFloat(prev.gstTotalInput),
              newTaxType,
              newDir,
              prev.itemDetails
            )
          );
        } else {
          Object.assign(
            next,
            recalcTotals(
              prev.itemDetails,
              newTaxType,
              newDir,
              prev.roundOffManual
            )
          );
        }
        return next;
      }

      // --- Anything else (address, state, etc.) ---
      return { ...prev, [name]: value };
    });
  };

  const handleCustomerSelect = (e) => {
    // Accept either { target: { value } } or a raw id
    const id = e && typeof e === "object" && e.target ? e.target.value : e;
    const cust = customerData.find((c) => String(c.id) === String(id));
    setFormData((prev) => ({
      ...prev,
      customerId: id,
      customerName: cust?.name || "",
      address: cust?.address || "",
      gstNo: cust?.gstNo || "",
      state: cust?.state || prev.state,
    }));
  };

  const handleItemChange = (id, field, value) => {
    setFormData((prev) => {
      const items = prev.itemDetails.map((it) => {
        if (it.id !== id) return it;
        const updated = { ...it, [field]: value };

        if (field === "grossWeight" || field === "netWeight") {
          updated[field] = roundTo3(value);
        }
        if (field === "rate") {
          updated.rate = roundTo2(value);
        }
        if (field === "amount") {
          updated.amount = roundTo2(value);
        }

        const net = parseFloat(field === "netWeight" ? value : updated.netWeight) || 0;
        const rate = parseFloat(field === "rate" ? value : updated.rate) || 0;
        const amt = parseFloat(field === "amount" ? value : updated.amount) || 0;

        if (field === "netWeight" || field === "rate") {
          if (net > 0 && rate > 0) updated.amount = roundTo2(net * rate);
        } else if (field === "amount") {
          if (net > 0 && amt > 0) updated.rate = roundTo2(amt / net);
        }
        return updated;
      });
      return {
        ...prev,
        itemDetails: items,
        ...recalcTotals(
          items,
          prev.taxType,
          prev.roundOffDirection,
          prev.entryMode === "weight" ? prev.roundOffManual : null
        ),
      };
    });
  };

  const addItemRow = () => {
    setFormData((prev) => {
      const newId = Math.max(...prev.itemDetails.map((i) => i.id), 0) + 1;
      const items = [...prev.itemDetails, getEmptyItem(newId)];
      return {
        ...prev,
        itemDetails: items,
        ...recalcTotals(items, prev.taxType, prev.roundOffDirection,
          prev.entryMode === "weight" ? prev.roundOffManual : null),
      };
    });
  };

  const removeItemRow = (id) => {
    if (formData.itemDetails.length <= 1) return;
    setFormData((prev) => {
      const items = prev.itemDetails.filter((it) => it.id !== id);
      return {
        ...prev,
        itemDetails: items,
        ...recalcTotals(items, prev.taxType, prev.roundOffDirection,
          prev.entryMode === "weight" ? prev.roundOffManual : null),
      };
    });
  };

  const resetForm = () => {
    const items = [getEmptyItem(1)];
    setFormData({
      billNo: generateBillNumber(),
      date: new Date().toISOString().split("T")[0],
      customerId: "",
      customerName: "",
      address: "",
      gstNo: "",
      state: "Gujarat",
      itemDetails: items,
      taxType: "cgst",
      roundOffDirection: "add",
      entryMode: "weight",
      gstTotalInput: "",
      taxableAmount: 0,
      cgst: 0,
      sgst: 0,
      igst: 0,
      roundOff: 0,
      roundOffManual: null,
      grandTotal: 0,
      amountInWords: "",
    });
  };

  const showAlert = useCallback((msg, type) => {
    setAlertMessage(msg);
    setAlertType(type);
    setTimeout(() => setAlertMessage(""), 3000);
  }, []);

  // -------- Submit --------
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.customerName) {
      showAlert("Please select customer", "error");
      return;
    }
    const valid = formData.itemDetails.filter(
      (i) => i.itemName.trim() !== "" && parseFloat(i.netWeight) > 0
    );
    if (valid.length === 0) {
      showAlert("Please add at least one valid item", "error");
      return;
    }

    setIsSaving(true);
    try {
      if (addEInvoice) {
        const payload = { ...formData };
        delete payload.roundOffManual;
        delete payload.gstTotalInput;
        if (payload.roundOffDirection === undefined) payload.roundOffDirection = "add";
        const res = await addEInvoice(payload);
        if (res?.success === false) {
          throw new Error(res?.error || "Save failed");
        }
      }
      showAlert("E-Invoice saved successfully!", "success");
      setIsDrawerOpen(false);
      resetForm();
    } catch (err) {
      console.error(err);
      showAlert(err.message || "Failed to save", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const generateEInvoiceHTML = useCallback((data) => {
    const items = (data.itemDetails || []).filter(
      (i) => i.itemName && parseFloat(i.netWeight) > 0
    );

    // ---------- helpers ----------
    const fmt = (n) =>
      (Number(n) || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    const wt = (n) => (n ? n.toFixed(3) + " grm" : "");
    const isIgst = data.taxType === "igst";

    // shared styles
    const FONT = "font-family:Arial, Helvetica, sans-serif;";
    const LBL = "font-size:10.5px;"; // small grey-less labels (Bill No., Dated ...)
    const VAL = "font-size:13px;";
    const BL = "border-left:1px solid #000;";

    // ---------- item rows ----------
    let totGross = 0, totNet = 0;
    let itemRows = "";
    items.forEach((it, idx) => {
      const g = parseFloat(it.grossWeight) || 0;
      const n = parseFloat(it.netWeight) || 0;
      const a = parseFloat(it.amount) || 0;
      totGross += g;
      totNet += n;
      itemRows += `
      <tr style="${VAL}">
        <td style="padding:3px 5px; vertical-align:top;">${idx + 1}</td>
        <td style="${BL} padding:3px 5px; vertical-align:top; font-weight:700;">${it.itemName}</td>
        <td style="${BL} padding:3px 5px; vertical-align:top;">${it.hsn || ""}</td>
        <td style="${BL} padding:3px 5px; vertical-align:top; text-align:right; font-weight:700;">${wt(g)}</td>
        <td style="${BL} padding:3px 5px; vertical-align:top; text-align:right; font-weight:700;">${wt(n)}</td>
        <td style="${BL} padding:3px 5px; vertical-align:top; text-align:right;">${it.rate || ""}</td>
        <td style="${BL} padding:3px 5px; vertical-align:top;">${it.per || "grm"}</td>
        <td style="${BL} padding:3px 5px; vertical-align:top; text-align:right; font-weight:700;">${fmt(a)}</td>
      </tr>`;
    });

    // empty filler row so the tax lines sit at the bottom (as in the PDF)
    const fillerH = Math.max(6, 130 - items.length * 20);
    const filler = `
      <tr>
        <td style="height:${fillerH}px;"></td>
        <td style="${BL}"></td><td style="${BL}"></td><td style="${BL}"></td>
        <td style="${BL}"></td><td style="${BL}"></td><td style="${BL}"></td><td style="${BL}"></td>
      </tr>`;

    // one "tax / round off" line inside the items table
    const adjRow = (label, rate, per, amount) => `
      <tr style="${VAL} font-weight:700;">
        <td></td>
        <td style="${BL} padding:3px 5px; text-align:right;">${label}</td>
        <td style="${BL}"></td><td style="${BL}"></td><td style="${BL}"></td>
        <td style="${BL} padding:3px 5px; text-align:right; font-weight:400;">${rate}</td>
        <td style="${BL} padding:3px 5px; font-weight:400;">${per}</td>
        <td style="${BL} padding:3px 5px; text-align:right;">${amount}</td>
      </tr>`;

    const taxLines = isIgst
      ? adjRow("IGST @ 3%", "3.00", "%", fmt(data.igst))
      : adjRow("CGST @ 1.5%", "1.50", "%", fmt(data.cgst)) +
      adjRow("SGST @ 1.5%", "1.50", "%", fmt(data.sgst));

    const roundOffVal = Number(data.roundOff) || 0;
    const roundOffText =
      roundOffVal < 0 ? `(-${Math.abs(roundOffVal).toFixed(2)})` : roundOffVal.toFixed(2);

    const amountInWordsStr = (data.amountInWords || "")
      .replace(/Rupees Only$/i, "Only")
      .trim();
    const grandTotal = fmt(data.grandTotal);
    const hsn = items[0]?.hsn || "711311";

    // ---------- tax summary ----------
    const S = "border-bottom:1px solid #000; padding:3px 5px;";
    const SL = BL;
    const sumCols = isIgst
      ? `<col style="width:22%"/><col style="width:22%"/><col style="width:12%"/><col style="width:20%"/><col style="width:24%"/>`
      : `<col style="width:17.2%"/><col style="width:15.4%"/><col style="width:9.6%"/><col style="width:13.3%"/><col style="width:9.7%"/><col style="width:13.3%"/><col style="width:21.5%"/>`;

    const taxHeader = isIgst
      ? `<tr>
        <th rowspan="2" style="${S} font-weight:400; text-align:center;">HSN/SAC</th>
        <th rowspan="2" style="${S} ${SL} font-weight:400; text-align:right;">Taxable Value</th>
        <th colspan="2" style="${S} ${SL} font-weight:400; text-align:center;">IGST</th>
        <th rowspan="2" style="${S} ${SL} font-weight:400; text-align:right;">Total Tax Amount</th>
      </tr>
      <tr>
        <th style="${S} ${SL} font-weight:400; text-align:center;">Rate</th>
        <th style="${S} ${SL} font-weight:400; text-align:right;">Amount</th>
      </tr>`
      : `<tr>
        <th rowspan="2" style="${S} font-weight:400; text-align:center;">HSN/SAC</th>
        <th rowspan="2" style="${S} ${SL} font-weight:400; text-align:right;">Taxable Value</th>
        <th colspan="2" style="${S} ${SL} font-weight:400; text-align:center;">CGST</th>
        <th colspan="2" style="${S} ${SL} font-weight:400; text-align:center;">SGST/UTGST</th>
        <th rowspan="2" style="${S} ${SL} font-weight:400; text-align:right;">Total Tax Amount</th>
      </tr>
      <tr>
        <th style="${S} ${SL} font-weight:400; text-align:center;">Rate</th>
        <th style="${S} ${SL} font-weight:400; text-align:right;">Amount</th>
        <th style="${S} ${SL} font-weight:400; text-align:center;">Rate</th>
        <th style="${S} ${SL} font-weight:400; text-align:right;">Amount</th>
      </tr>`;

    const totalTax = isIgst ? Number(data.igst) || 0 : (Number(data.cgst) || 0) + (Number(data.sgst) || 0);

    const taxSummary = isIgst
      ? `<tr style="${VAL}">
        <td style="${S} text-align:left;">${hsn}</td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.taxableAmount)}</td>
        <td style="${S} ${SL} text-align:center;">3.00%</td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.igst)}</td>
        <td style="${S} ${SL} text-align:right;">${fmt(totalTax)}</td>
      </tr>
      <tr style="${VAL} font-weight:700;">
        <td style="${S} text-align:right;">Total</td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.taxableAmount)}</td>
        <td style="${S} ${SL}"></td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.igst)}</td>
        <td style="${S} ${SL} text-align:right;">${fmt(totalTax)}</td>
      </tr>`
      : `<tr style="${VAL}">
        <td style="${S} text-align:left;">${hsn}</td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.taxableAmount)}</td>
        <td style="${S} ${SL} text-align:center;">1.50%</td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.cgst)}</td>
        <td style="${S} ${SL} text-align:center;">1.50%</td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.sgst)}</td>
        <td style="${S} ${SL} text-align:right;">${fmt(totalTax)}</td>
      </tr>
      <tr style="${VAL} font-weight:700;">
        <td style="${S} text-align:right;">Total</td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.taxableAmount)}</td>
        <td style="${S} ${SL}"></td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.cgst)}</td>
        <td style="${S} ${SL}"></td>
        <td style="${S} ${SL} text-align:right;">${fmt(data.sgst)}</td>
        <td style="${S} ${SL} text-align:right;">${fmt(totalTax)}</td>
      </tr>`;

    // ---------- right-hand reference grid cell ----------
    const cell = (label, value = "", opts = "") => `
      <div style="${LBL} padding:4px 6px; box-sizing:border-box; height:58px; ${opts}">
        <div>${label}</div>
        ${value ? `<div style="font-size:12.5px; font-weight:700; margin-top:1px;">${value}</div>` : ""}
      </div>`;
    const R = "border-bottom:1px solid #000;";
    const RR = "border-bottom:1px solid #000; border-right:1px solid #000;";

    // ---------- party block (consignee / buyer) ----------
    const colonRow = (label, value) =>
      `<div><span style="display:inline-block; width:100px;">${label}</span>: ${value}</div>`;

    const party = (title, borderBottom) => `
      <div style="padding:3px 6px; box-sizing:border-box; line-height:1.42; ${VAL} ${borderBottom ? "border-bottom:1px solid #000;" : ""}">
        <div style="${LBL} line-height:1.3;">${title}</div>
        <div style="font-weight:700;">${data.customerName || ""}</div>
        <div style="white-space:pre-line;">${data.address || ""}</div>
        ${colonRow("GSTIN/UIN", data.gstNo || "")}
        ${colonRow("State Name", data.state || "")}
      </div>`;

    return `
<div id="einvoiceBillContent" style="${FONT} width:820px; margin:0 auto; background:#fff; color:#000; padding:6px 0; box-sizing:border-box;">

  <!-- TITLE -->
  <div style="text-align:center; font-size:17px; font-weight:700; margin-bottom:6px;">Tax Invoice</div>

  <div style="border:1px solid #000;">

    <!-- ===== TOP BLOCK ===== -->
    <div style="display:flex;">

      <!-- LEFT : seller / consignee / buyer -->
      <div style="width:49.5%; border-right:1px solid #000; box-sizing:border-box;">
        <div style="padding:3px 6px; line-height:1.42; ${VAL} border-bottom:1px solid #000;">
          <div style="font-size:15px; font-weight:700;">AURUM SILVER</div>
          <div>PLOT NO 7, DHRUVPARK SOCIETY</div>
          <div>NEAR NAHER ROAD, GODADARA</div>
          <div>SURAT, GUJARAT - 395010</div>
          ${colonRow("GSTIN/UIN", "24AQIPV6570G3ZE")}
          ${colonRow("State Name", "Gujarat.")}
        </div>
        ${party("Consignee (Ship to)", true)}
        ${party("Buyer (Bill to)", false)}
      </div>

      <!-- RIGHT : reference grid (2 cols x 7 rows) -->
      <div style="width:50.5%; box-sizing:border-box;">
        <div style="display:grid; grid-template-columns:51% 49%;">
          ${cell("Bill No.", data.billNo || "-", RR)}
          ${cell("Dated", formatDateDisplay(data.date), R)}
          ${cell("Delivery Note", "", RR)}
          ${cell("Mode/Terms of Payment", "", R)}
          ${cell("Reference No. &amp; Date.", "", RR)}
          ${cell("Other References", "", R)}
          ${cell("Buyer's Order No.", "", RR)}
          ${cell("Dated", "", R)}
          ${cell("Dispatch Doc No.", "", RR)}
          ${cell("Delivery Note Date", "", R)}
          ${cell("Dispatched through", "", RR)}
          ${cell("Destination", "", R)}
          <div style="grid-column:1 / span 2;">${cell("Terms of Delivery", "", "")}</div>
        </div>
      </div>
    </div>

    <!-- ===== ITEMS TABLE ===== -->
    <table style="width:100%; border-collapse:collapse; table-layout:fixed; border-top:1px solid #000;">
      <colgroup>
        <col style="width:7%"/><col style="width:23%"/><col style="width:12.5%"/><col style="width:12.5%"/>
        <col style="width:12.5%"/><col style="width:10.5%"/><col style="width:6%"/><col style="width:16%"/>
      </colgroup>
      <thead>
        <tr style="${VAL}">
          <th style="font-weight:400; text-align:left; padding:3px 5px; border-bottom:1px solid #000;">SI No.</th>
          <th style="${BL} font-weight:400; text-align:left; padding:3px 5px; border-bottom:1px solid #000;">Description of Goods</th>
          <th style="${BL} font-weight:400; text-align:left; padding:3px 5px; border-bottom:1px solid #000;">HSN/SAC</th>
          <th style="${BL} font-weight:400; text-align:right; padding:3px 5px; border-bottom:1px solid #000;">Gross Weight</th>
          <th style="${BL} font-weight:400; text-align:right; padding:3px 5px; border-bottom:1px solid #000;">Net Weight</th>
          <th style="${BL} font-weight:400; text-align:right; padding:3px 5px; border-bottom:1px solid #000;">Rate</th>
          <th style="${BL} font-weight:400; text-align:left; padding:3px 5px; border-bottom:1px solid #000;">per</th>
          <th style="${BL} font-weight:400; text-align:right; padding:3px 5px; border-bottom:1px solid #000;">Amount</th>
        </tr>
      </thead>
      <tbody>
        ${itemRows}
        ${filler}
        ${taxLines}
        ${adjRow("Round off", "", "", roundOffText)}
        <tr style="${VAL}">
          <td style="border-top:1px solid #000; border-bottom:1px solid #000;"></td>
          <td style="${BL} border-top:1px solid #000; border-bottom:1px solid #000; padding:3px 5px; text-align:right;">Total</td>
          <td style="${BL} border-top:1px solid #000; border-bottom:1px solid #000;"></td>
          <td style="${BL} border-top:1px solid #000; border-bottom:1px solid #000; padding:3px 5px; text-align:right;">${wt(totGross)}</td>
          <td style="${BL} border-top:1px solid #000; border-bottom:1px solid #000; padding:3px 5px; text-align:right;">${wt(totNet)}</td>
          <td style="${BL} border-top:1px solid #000; border-bottom:1px solid #000;"></td>
          <td style="${BL} border-top:1px solid #000; border-bottom:1px solid #000;"></td>
          <td style="${BL} border-top:1px solid #000; border-bottom:1px solid #000; padding:3px 5px; text-align:right;">₹ ${grandTotal}</td>
        </tr>
      </tbody>
    </table>

    <!-- ===== AMOUNT IN WORDS ===== -->
    <div style="padding:4px 6px; border-bottom:1px solid #000;">
      <div style="display:flex; justify-content:space-between; ${LBL}">
        <span>Amount Chargeable (in words)</span>
        <span style="font-size:12px;">E. &amp; O.E</span>
      </div>
      <div style="font-size:13px; font-weight:700; margin-top:3px;">INR ${amountInWordsStr}</div>
    </div>

    <!-- ===== TAX SUMMARY ===== -->
    <table style="width:100%; border-collapse:collapse; table-layout:fixed; ${VAL}">
      <colgroup>${sumCols}</colgroup>
      <thead>${taxHeader}</thead>
      <tbody>${taxSummary}</tbody>
    </table>

    <!-- ===== BOTTOM BLOCK ===== -->
    <div style="display:flex; min-height:185px;">

      <!-- left : words / PAN / GSTIN / declaration -->
      <div style="width:50%; padding:4px 6px; box-sizing:border-box;">
        <div style="font-size:13px; line-height:1.3;">
          Tax Amount (in words) : <b>INR ${amountInWordsStr}</b>
        </div>
        <div style="font-size:13px; line-height:1.42; margin-top:3px;">
          <div><b style="display:inline-block; width:120px;">Company's PAN</b><b>: AQIPV6570G</b></div>
          <div><b style="display:inline-block; width:120px;">Company's GSTIN</b><b>: 24AQIPV6570G3ZE</b></div>
        </div>
        <div style="${LBL} margin-top:4px;">Declaration</div>
        <div style="font-size:10.5px; line-height:1.2;">
          I/we hereby certify that my/our registration certificate under the maharashtra value added tax act2002 is in force on the date on witch the sale of the goods specified in this tax invoice is made by me/us &amp; that the transaction if the sale covered by this tax invoice has been effected by me/us &amp; it shall be accounted for in turnover f sale while filing of return &amp; due tax, if, any payable on the sale has been paid or shall be paid.
        </div>
      </div>

      <!-- right : bank details + signatory -->
      <div style="width:50%; padding:4px 6px; box-sizing:border-box; display:flex; flex-direction:column; justify-content:space-between;">
        <div>
          <div style="${LBL} margin-bottom:4px;">Company's Bank Details</div>
          <div style="font-size:13px; line-height:1.42;">
            <div><b style="display:inline-block; width:112px;">Firm Name</b><b>: AURUM SILVER</b></div>
            <div><b style="display:inline-block; width:112px;">Bank Name</b><b>: IDBI BANK</b></div>
            <div><b style="display:inline-block; width:112px;">A/c No.</b><b>: 0324102000084686</b></div>
            <div><b style="display:inline-block; width:112px;">Branch</b><b>: PUNA KUMBHARIYA (SURAT)</b></div>
            <div><b style="display:inline-block; width:112px;">IFS Code</b><b>: IBKL0000324</b></div>
          </div>
        </div>
        <div style="text-align:right;">
          <div style="font-size:13px; font-weight:700; margin-bottom:26px;">for AURUM SILVER</div>
          <div style="font-size:12px;">Authorised Signatory</div>
        </div>
      </div>
    </div>
  </div>
</div>`;
  }, []);

  const openPrintWindow = useCallback((html, autoPrint = true) => {
    const w = window.open("", "_blank", "width=900,height=700");
    if (!w) {
      showAlert("Please allow popups to print", "error");
      return;
    }
    w.document.write(`
      <!DOCTYPE html>
      <html><head><title>E-Invoice</title>
      <style>${getBillStyles()}</style>
      </head><body>${html}
      <script>
        window.onload = function() {
          ${autoPrint ? "window.print(); setTimeout(function(){ window.close(); }, 800);" : ""}
        };
      </script>
      </body></html>`);
    w.document.close();
  }, [showAlert]);

  const handlePrint = useCallback((row) => {
    const data = row || formData;
    const valid = (data.itemDetails || []).filter(
      (i) => i.itemName && parseFloat(i.netWeight) > 0
    );
    if (valid.length === 0) {
      showAlert("No items to print", "error");
      return;
    }
    const html = generateEInvoiceHTML(data);
    setPrintData(html);
    setTimeout(() => openPrintWindow(html, true), 200);
  }, [formData, generateEInvoiceHTML, openPrintWindow, showAlert]);

  const handlePreview = useCallback(() => {
    const valid = formData.itemDetails.filter(
      (i) => i.itemName && parseFloat(i.netWeight) > 0
    );
    if (valid.length === 0) {
      showAlert("No items to preview", "error");
      return;
    }
    const html = generateEInvoiceHTML(formData);
    setPrintData(html);
    openPrintWindow(html, false);
  }, [formData, generateEInvoiceHTML, openPrintWindow, showAlert]);

  const handleRefresh = useCallback(async () => {
    setIsPageLoading(true);
    try {
      if (refresh) await refresh();
    } catch (e) {
      console.warn("Refresh failed:", e);
    } finally {
      setTimeout(() => setIsPageLoading(false), 400);
    }
  }, [refresh]);

  // -------- AG Grid columns --------
  const columnDefs = useMemo(
    () => [
      {
        headerName: "#",
        valueGetter: "node.rowIndex + 1",
        width: 60,
        pinned: "left",
        sortable: false,
        filter: false,
        cellStyle: {
          textAlign: "center",
          fontWeight: 700,
          color: isDark ? "#94a3b8" : "#64748b",
        },
      },
      {
        field: "billNo",
        headerName: "Bill No",
        width: 120,
        filter: "agTextColumnFilter",
        cellStyle: { fontWeight: 700, color: isDark ? "#93c5fd" : "#1d4ed8" },
      },
      {
        field: "customerName",
        headerName: "Customer",
        width: 200,
        filter: "agTextColumnFilter",
        cellStyle: { fontWeight: 600 },
      },
      {
        field: "gstNo",
        headerName: "GST No",
        width: 180,
        filter: "agTextColumnFilter",
        cellStyle: {
          fontFamily: "monospace",
          fontSize: "13px",
          color: isDark ? "#cbd5e1" : "#475569",
        },
      },
      {
        field: "state",
        headerName: "State",
        width: 140,
        filter: "agTextColumnFilter",
        cellStyle: { color: isDark ? "#cbd5e1" : "#475569" },
      },
      {
        field: "date",
        headerName: "Date",
        width: 130,
        filter: "agDateColumnFilter",
        valueFormatter: (p) => formatDateDisplay(p.value),
        cellStyle: { fontWeight: 500 },
      },
      {
        field: "itemDetails",
        headerName: "Items",
        width: 90,
        filter: false,
        valueGetter: (p) =>
          p.data?.itemDetails?.filter(
            (i) => i.itemName && parseFloat(i.netWeight) > 0
          ).length || 0,
        cellStyle: { textAlign: "center", fontWeight: 700 },
      },
      {
        field: "grandTotal",
        headerName: "Grand Total",
        width: 160,
        filter: "agNumberColumnFilter",
        valueFormatter: (p) =>
          `₹ ${(Number(p.value) || 0).toLocaleString("en-IN", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`,
        cellStyle: {
          textAlign: "right",
          fontWeight: 800,
          color: isDark ? "#34d399" : "#059669",
        },
      },
      {
        headerName: "Actions",
        width: 100,
        filter: false,
        sortable: false,
        cellRenderer: ActionsRenderer,
        pinned: "right",
        cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
        cellRendererParams: { context: { handlePrint } },
      },
    ],
    [handlePrint, isDark]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 90,
      cellStyle: { display: "flex", alignItems: "center" },
      suppressMenu: false,
      floatingFilter: false,
    }),
    []
  );

  // -------- Search filter --------
  // useEffect(() => {
  //   if (!isGridReady || !gridRef.current?.api) return;
  //   const t = setTimeout(() => {
  //     const api = gridRef.current.api;
  //     if (searchTerm.trim()) {
  //       const fm = {};
  //       ["customerName", "billNo", "gstNo", "state"].forEach((c) => {
  //         fm[c] = { filterType: "text", type: "contains", filter: searchTerm };
  //       });
  //       api.setFilterModel(fm);
  //     } else {
  //       api.setFilterModel(null);
  //     }
  //   }, 300);
  //   return () => clearTimeout(t);
  // }, [searchTerm, isGridReady]);

  const loadingOverlayComponent = useMemo(
    () =>
      function LO() {
        return (
          <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CircularProgress size={40} sx={{ color: "#2563EB" }} />
          </Box>
        );
      },
    []
  );

  const noRowsOverlayComponent = useMemo(
    () =>
      function NR() {
        return (
          <Box sx={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 1 }}>
            <Receipt sx={{ fontSize: 40, color: isDark ? "#475569" : "#cbd5e1" }} />
            <Typography sx={{ color: isDark ? "#a0a0a0" : "#666", fontWeight: 600 }}>
              {searchTerm ? "No invoices matching your search" : "No E-Invoices yet"}
            </Typography>
            {!searchTerm && (
              <Typography variant="caption" sx={{ color: isDark ? "#64748b" : "#94a3b8" }}>
                Click "New E-Invoice" to create your first invoice
              </Typography>
            )}
          </Box>
        );
      },
    [isDark, searchTerm]
  );

  const isLoading = contextLoading || isPageLoading;
  if (isLoading) {
    return <EInvoiceSkeleton isDark={isDark} />;
  }

  return (
    <Box sx={{ minHeight: "100vh", py: 2, color: isDark ? "#e0e0e0" : "#1a1a1a" }}>
      <Container maxWidth="xl">
        {alertMessage && (
          <Alert
            severity={alertType}
            sx={{ mb: 3 }}
            onClose={() => setAlertMessage("")}
          >
            {alertMessage}
          </Alert>
        )}

        {/* Header */}
        <Card
          sx={{
            mb: 3,
            mt: 1,
            background: isDark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.2)",
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={2}
              sx={{ alignItems: "center" }}
            >
              <Box sx={{ mb: 1 }}>
                <Typography
                  variant="h3"
                  sx={{
                    fontWeight: 800,
                    mb: 0.5,
                    color: isDark ? "#e0e0e0" : "#1a1a1a",
                  }}
                >
                  E-Invoice
                </Typography>
                <Typography
                  sx={{ color: isDark ? "#a0a0a0" : "#666", fontSize: "1rem" }}
                >
                  Total Invoices:{" "}
                  <Chip label={eInvoices?.length || 0} color="primary" size="small" />
                </Typography>
              </Box>

              <TextField
                placeholder="Search by Customer, Bill No, GST..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
                sx={{ flex: 1, minWidth: 250 }}
              />

              <Button
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                variant="outlined"
                size="small"
                sx={{
                  borderColor: "#4b8cb7",
                  color: isDark ? "#a0c1e0" : "#2a3f7a",
                }}
              >
                Refresh
              </Button>

              <Button
                startIcon={<AddIcon />}
                onClick={() => {
                  resetForm();
                  setIsDrawerOpen(true);
                }}
                variant="contained"
                size="small"
                sx={{
                  flex: { xs: 1, sm: "none" },
                  background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                  whiteSpace: "nowrap",
                }}
              >
                New E-Invoice
              </Button>
            </Stack>
          </CardContent>
        </Card>

        {/* Grid */}
        <Card
          elevation={0}
          sx={{
            borderRadius: "20px",
            overflow: "hidden",
            height: "calc(100vh - 400px)",
            minHeight: 500,
          }}
        >
          <Card
            elevation={0}
            sx={{
              borderRadius: "20px",
              overflow: "hidden",
              position: "relative",
              height: "calc(100vh - 400px)",
              minHeight: 500,
              background: isDark
                ? "linear-gradient(180deg, #1a1a2e 0%, #16161a 100%)"
                : "linear-gradient(180deg, #ffffff 0%, rgba(248, 250, 252, 0.26) 100%)",
              border: isDark
                ? "1px solid rgba(255, 255, 255, 0.06)"
                : "1px solid rgba(0, 0, 0, 0.04)",
              boxShadow: isDark
                ? "0 4px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.05) inset"
                : "0 4px 24px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(255, 255, 255, 0.8) inset",
            }}
          >
            <CardContent sx={{ p: 0, height: "100%" }}>
              <Box
                className={isDark ? "ag-theme-alpine-dark" : "ag-theme-alpine"}
                sx={getGridSx(isDark)}
              >
                <AgGridReact
                  ref={gridRef}
                  columnDefs={columnDefs}
                  rowData={eInvoices || []}
                  defaultColDef={defaultColDef}
                  quickFilterText={searchTerm}
                  loadingOverlayComponent={loadingOverlayComponent}
                  noRowsOverlayComponent={noRowsOverlayComponent}
                  rowHeight={64}
                  headerHeight={52}
                  animateRows
                  enableCellTextSelection
                  ensureDomOrder
                  suppressMovableColumns
                  suppressCellFocus
                  suppressContextMenu
                  rowSelection="single"
                  context={{ handlePrint }}
                  onGridReady={(p) => {
                    p.api.sizeColumnsToFit();
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Card>

        <Drawer
          anchor="right"
          open={isDrawerOpen}
          onClose={() => !isSaving && setIsDrawerOpen(false)}
          sx={{ zIndex: (t) => t.zIndex.drawer + 2 }}
          PaperProps={{
            sx: {
              width: { xs: "100%", sm: 680 },
              display: "flex",
              flexDirection: "column",
              color: isDark ? "#e0e0e0" : "#1a1a1a",
              background: isDark
                ? "linear-gradient(180deg, #1a1a2e 0%, #16161a 100%)"
                : "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)",
            },
          }}
        >
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}
          >
            {/* ============ HEADER ============ */}
            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                position: "sticky",
                top: 0,
                zIndex: 10,
                borderBottom: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.06)",
                background: isDark ? "linear-gradient(180deg, #252536 0%, #1e1e2d 100%)" : "linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    display: "grid",
                    placeItems: "center",
                    background: alpha(ACCENT, 0.15),
                  }}
                >
                  <Receipt sx={{ color: ACCENT }} />
                </Box>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    New E-Invoice
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary" }}>
                    Fill in the details below, then save
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={() => setIsDrawerOpen(false)}
                disabled={isSaving}
              >
                <CloseIcon />
              </IconButton>
            </Box>

            {/* ============ SCROLLABLE BODY ============ */}
            <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", px: 3, py: 3 }}>
              {/* ---- Invoice details ---- */}
              <Section
                isDark={isDark}
                title="Invoice Details"
                icon={<Receipt sx={{ color: ACCENT, fontSize: 20 }} />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Bill No"
                      name="billNo"
                      value={formData.billNo}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      helperText={`Last bill: ${getLastBillNumber()}`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Date"
                        value={
                          formData.date ? parse(formData.date, "yyyy-MM-dd", new Date()) : null
                        }
                        onChange={(v) =>
                          v && setFormData((p) => ({ ...p, date: format(v, "yyyy-MM-dd") }))
                        }
                        format="dd/MM/yyyy"
                        slotProps={{ textField: { fullWidth: true, size: "small" } }}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              </Section>

              {/* ---- Customer ---- */}
              <Section
                isDark={isDark}
                title="Customer"
                icon={<Person sx={{ color: ACCENT, fontSize: 20 }} />}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sx={{ width: "300px" }}>
                    <Autocomplete
                      fullWidth
                      size="small"
                      options={customerData || []}
                      openOnFocus
                      autoHighlight
                      selectOnFocus
                      handleHomeEndKeys
                      getOptionLabel={(option) =>
                        typeof option === "string" ? option : option?.name || ""
                      }
                      value={
                        customerData.find((c) => String(c.id) === String(formData.customerId)) ||
                        null
                      }
                      onChange={(event, newValue) => {
                        if (!newValue) {
                          setFormData((prev) => ({
                            ...prev,
                            customerId: "",
                            customerName: "",
                            address: "",
                            gstNo: "",
                          }));
                          return;
                        }
                        handleCustomerSelect({ target: { value: newValue.id } });
                      }}
                      // Search by name / mobile / GST. When a customer is already
                      // selected and the box just shows their name, list everyone.
                      filterOptions={(options, { inputValue }) => {
                        const q = inputValue.trim().toLowerCase();
                        const selected = customerData.find(
                          (c) => String(c.id) === String(formData.customerId)
                        );
                        if (!q || (selected && (selected.name || "").toLowerCase() === q)) {
                          return options;
                        }
                        return options.filter((c) => {
                          const name = (c.name || "").toLowerCase();
                          const contact = String(
                            c.contact ?? c.phone ?? c.contactNo ?? ""
                          ).toLowerCase();
                          const gst = (c.gstNo || "").toLowerCase();
                          return name.includes(q) || contact.includes(q) || gst.includes(q);
                        });
                      }}
                      renderOption={(props, option) => {
                        const { key, ...rest } = props;
                        const contact = option.contact ?? option.phone ?? option.contactNo ?? "";
                        return (
                          <Box
                            component="li"
                            key={key}
                            {...rest}
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start !important",
                              gap: 0.25,
                              py: "8px !important",
                            }}
                          >
                            <Typography sx={{ fontWeight: 600, fontSize: "0.9rem", lineHeight: 1.3 }}>
                              {option.name}
                            </Typography>
                            {(contact || option.gstNo) && (
                              <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                {contact ? `Mobile: ${contact}` : ""}
                                {contact && option.gstNo ? "   |   " : ""}
                                {option.gstNo ? `GST: ${option.gstNo}` : ""}
                              </Typography>
                            )}
                          </Box>
                        );
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Customer"
                          required
                          placeholder="Search by name, mobile or GST"
                          InputLabelProps={{ ...params.InputLabelProps, shrink: true }}
                        />
                      )}
                      isOptionEqualToValue={(option, value) =>
                        String(option?.id) === String(value?.id)
                      }
                      noOptionsText="No customer matches your search"
                      slotProps={{
                        popper: { sx: { zIndex: (t) => t.zIndex.modal + 10 } },
                        paper: { sx: { maxHeight: 320 } },
                      }}
                      ListboxProps={{ style: { maxHeight: 280 } }}
                    />
                  </Grid>
                  <Grid item xs={12} sx={{ width: "300px" }}>
                    <TextField
                      label="Address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      multiline
                      rows={2}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: "300px" }}>
                    <TextField
                      label="GST No"
                      name="gstNo"
                      value={formData.gstNo}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: "300px" }}>
                    <TextField
                      select
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                    >
                      {STATE_LIST.map((s) => (
                        <MenuItem key={s} value={s}>
                          {s}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                </Grid>
              </Section>

              {/* ---- Entry mode ---- */}
              <Section isDark={isDark} title="How do you want to enter the amount?">
                <RadioGroup
                  row
                  name="entryMode"
                  value={formData.entryMode}
                  onChange={handleInputChange}
                  sx={{ gap: 1.5, flexWrap: "wrap" }}
                >
                  {[
                    { value: "weight", label: "Net weight × metal rate" },
                    { value: "gstAmount", label: "Direct GST amount" },
                  ].map((opt) => {
                    const selected = formData.entryMode === opt.value;
                    return (
                      <Paper
                        key={opt.value}
                        variant="outlined"
                        sx={{
                          flex: "1 1 220px",
                          m: 0,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          borderColor: selected ? ACCENT : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                          background: selected ? alpha(ACCENT, isDark ? 0.15 : 0.08) : "transparent",
                          transition: "all .15s",
                        }}
                      >
                        <FormControlLabel
                          value={opt.value}
                          control={<Radio size="small" sx={{ "&.Mui-checked": { color: ACCENT } }} />}
                          label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>}
                          sx={{ m: 0, width: "100%" }}
                        />
                      </Paper>
                    );
                  })}
                </RadioGroup>

                {formData.entryMode === "gstAmount" && (
                  <TextField
                    label="GST amount (final, tax-inclusive) ₹"
                    name="gstTotalInput"
                    value={formData.gstTotalInput}
                    onChange={handleInputChange}
                    fullWidth
                    size="small"
                    type="number"
                    inputProps={{ step: "0.01" }}
                    helperText="Taxable amount, CGST/SGST (or IGST), round off and metal rate are calculated for you."
                    sx={{ mt: 2 }}
                  />
                )}
              </Section>

              {/* ---- Items ---- */}
              <Section
                isDark={isDark}
                title={`Items (${formData.itemDetails.length})`}
                icon={<Inventory sx={{ color: ACCENT, fontSize: 20 }} />}
                action={
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addItemRow}
                    sx={{ color: ACCENT, fontWeight: 600 }}
                  >
                    Add item
                  </Button>
                }
              >
                <Stack spacing={2}>
                  {formData.itemDetails.map((item, idx) => (
                    <Paper
                      key={item.id}
                      variant="outlined"
                      sx={{
                        borderRadius: 1,
                        overflow: "hidden",
                        borderColor: (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                        background: isDark ? "rgba(255,255,255,0.03)" : "#f8fafc",
                      }}
                    >
                      {/* item header strip */}
                      <Box
                        sx={{
                          px: 2,
                          py: 0.75,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                          background: isDark ? "rgba(255,255,255,0.05)" : "#f1f5f9",
                          borderBottom: `1px solid ${(isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)")}`,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          Item {idx + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => removeItemRow(item.id)}
                          disabled={formData.itemDetails.length <= 1}
                          sx={{ color: "#ef4444" }}
                          aria-label={`Remove item ${idx + 1}`}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>

                      <Grid container spacing={1.5} sx={{ p: 2, pt: 2.5 }}>
                        <Grid item xs={12} sm={8}>
                          <TextField
                            label="Item name"
                            value={item.itemName}
                            onChange={(e) => handleItemChange(item.id, "itemName", e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <TextField
                            label="HSN/SAC"
                            value={item.hsn}
                            onChange={(e) => handleItemChange(item.id, "hsn", e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Gross wt"
                            value={item.grossWeight}
                            onChange={(e) => handleItemChange(item.id, "grossWeight", e.target.value)}
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ step: "0.001" }}
                            InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Net wt"
                            value={item.netWeight}
                            onChange={(e) => handleItemChange(item.id, "netWeight", e.target.value)}
                            fullWidth
                            size="small"
                            type="number"
                            inputProps={{ step: "0.001" }}
                            InputProps={{ endAdornment: <InputAdornment position="end">g</InputAdornment> }}
                          />
                        </Grid>
                        <Grid item xs={6} sm={4}>
                          <TextField
                            label="Rate"
                            value={
                              formData.entryMode === "gstAmount"
                                ? (parseFloat(item.rate) || 0).toFixed(2)
                                : item.rate
                            }
                            onChange={(e) => handleItemChange(item.id, "rate", e.target.value)}
                            fullWidth
                            size="small"
                            type={formData.entryMode === "gstAmount" ? "text" : "number"}
                            inputProps={{ step: "0.01" }}
                            InputProps={{
                              readOnly: formData.entryMode === "gstAmount",
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            helperText={formData.entryMode === "gstAmount" ? "Auto-calculated" : " "}
                          />
                        </Grid>
                        <Grid item xs={6} sm={2}>
                          <TextField
                            label="Per"
                            value={item.per}
                            onChange={(e) => handleItemChange(item.id, "per", e.target.value)}
                            fullWidth
                            size="small"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            label={formData.entryMode === "weight" ? "Amount" : "Amount (auto)"}
                            value={
                              formData.entryMode === "weight"
                                ? item.amount
                                : (parseFloat(item.amount) || 0).toFixed(2)
                            }
                            onChange={(e) => handleItemChange(item.id, "amount", e.target.value)}
                            fullWidth
                            size="small"
                            type={formData.entryMode === "weight" ? "number" : "text"}
                            inputProps={{ step: "0.01" }}
                            InputProps={{
                              readOnly: formData.entryMode !== "weight",
                              startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                            }}
                            sx={{ "& input": { fontWeight: 700, color: (isDark ? "#34d399" : "#059669") } }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                </Stack>
              </Section>

              {/* ---- Tax & round off ---- */}
              <Section isDark={isDark} title="Tax & Round Off">
                <RadioGroup
                  row
                  name="taxType"
                  value={formData.taxType}
                  onChange={handleInputChange}
                  sx={{ gap: 1.5, mb: 2, flexWrap: "wrap" }}
                >
                  {[
                    { value: "cgst", label: "CGST + SGST (1.5% each)" },
                    { value: "igst", label: "IGST (3%)" },
                  ].map((opt) => {
                    const selected = formData.taxType === opt.value;
                    return (
                      <Paper
                        key={opt.value}
                        variant="outlined"
                        sx={{
                          flex: "1 1 220px",
                          m: 0,
                          px: 1.5,
                          py: 0.5,
                          borderRadius: 2,
                          borderColor: selected ? ACCENT : (isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"),
                          background: selected ? alpha(ACCENT, isDark ? 0.15 : 0.08) : "transparent",
                        }}
                      >
                        <FormControlLabel
                          value={opt.value}
                          control={<Radio size="small" sx={{ "&.Mui-checked": { color: ACCENT } }} />}
                          label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>}
                          sx={{ m: 0, width: "100%" }}
                        />
                      </Paper>
                    );
                  })}
                </RadioGroup>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      select
                      label="Round-off direction"
                      name="roundOffDirection"
                      value={formData.roundOffDirection}
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      disabled={formData.entryMode === "gstAmount"}
                      helperText={
                        formData.entryMode === "gstAmount"
                          ? "Derived from the GST amount"
                          : "Used when the manual value is empty"
                      }
                    >
                      <MenuItem value="add">Add (+)</MenuItem>
                      <MenuItem value="subtract">Subtract (−)</MenuItem>
                    </TextField>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      label="Round-off value"
                      name="roundOffManual"
                      value={
                        formData.roundOffManual === null
                          ? formData.roundOff.toFixed(2)
                          : String(formData.roundOffManual)
                      }
                      onChange={handleInputChange}
                      fullWidth
                      size="small"
                      type="number"
                      inputProps={{ step: "0.01" }}
                      disabled={formData.entryMode === "gstAmount"}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              onClick={() => {
                                setFormData((prev) => {
                                  const next = { ...prev, roundOffManual: null };
                                  Object.assign(
                                    next,
                                    recalcTotals(
                                      prev.itemDetails,
                                      prev.taxType,
                                      prev.roundOffDirection,
                                      null
                                    )
                                  );
                                  return next;
                                });
                              }}
                              disabled={formData.entryMode === "gstAmount"}
                              sx={{ minWidth: 0, px: 1, color: ACCENT, fontWeight: 600 }}
                            >
                              Nearest
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Section>

              {/* ---- Summary ---- */}
              <Paper
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  overflow: "hidden",
                  borderColor: alpha(ACCENT, 0.5),
                  background: isDark ? "rgba(255,255,255,0.04)" : "#ffffff",
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Summary
                  </Typography>
                  <Stack spacing={0.75}>
                    {[
                      { label: "Taxable amount", value: formData.taxableAmount },
                      ...(formData.taxType === "cgst"
                        ? [
                          { label: "CGST @ 1.5%", value: formData.cgst },
                          { label: "SGST @ 1.5%", value: formData.sgst },
                        ]
                        : [{ label: "IGST @ 3%", value: formData.igst }]),
                      { label: "Round off", value: formData.roundOff },
                    ].map((row) => (
                      <Box key={row.label} sx={{ display: "flex", justifyContent: "space-between" }}>
                        <Typography variant="body2" color="text.secondary">
                          {row.label}
                        </Typography>
                        <Typography variant="body2" sx={{ fontVariantNumeric: "tabular-nums" }}>
                          ₹ {row.value.toFixed(2)}
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    background: alpha(ACCENT, isDark ? 0.18 : 0.1),
                    borderTop: `1px solid ${alpha(ACCENT, 0.4)}`,
                  }}
                >
                  <Typography sx={{ fontWeight: 700 }}>Grand total</Typography>
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: "1.25rem",
                      color: (isDark ? "#34d399" : "#059669"),
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    ₹ {formData.grandTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Paper>
            </Box>

            {/* ============ STICKY FOOTER ============ */}
            <Box
              sx={{
                px: 3,
                py: 2,
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                borderTop: `1px solid ${(isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)")}`,
                background: isDark ? "linear-gradient(180deg, #252536 0%, #1e1e2d 100%)" : "linear-gradient(180deg, #f1f5f9 0%, #e2e8f0 100%)",
              }}
            >
              <Box sx={{ display: { xs: "none", sm: "block" }, mr: "auto" }}>
                <Typography variant="caption" color="text.secondary">
                  Grand total
                </Typography>
                <Typography sx={{ fontWeight: 800, color: (isDark ? "#34d399" : "#059669"), lineHeight: 1.1 }}>
                  ₹ {formData.grandTotal.toFixed(2)}
                </Typography>
              </Box>
              <Button
                onClick={() => setIsDrawerOpen(false)}
                disabled={isSaving}
                variant="text"
                color="inherit"
                sx={{ borderRadius: 2 }}
              >
                Cancel
              </Button>
              <Button
                onClick={handlePreview}
                variant="outlined"
                startIcon={<PreviewIcon />}
                sx={{ borderRadius: 2, borderColor: ACCENT, color: ACCENT }}
              >
                Preview
              </Button>
              <Button
                type="submit"
                variant="contained"
                startIcon={<SaveIcon />}
                disabled={isSaving}
                sx={{
                  borderRadius: 2,
                  px: 3,
                  background: "linear-gradient(135deg, #2563EB, #1D4ED8)",
                }}
              >
                {isSaving ? "Saving…" : "Save invoice"}
              </Button>
            </Box>
          </form>
        </Drawer>

        {isDrawerOpen && (
          <Backdrop sx={{ zIndex: (t) => t.zIndex.drawer - 1 }} open />
        )}
      </Container>

      {printData && (
        <div style={{ display: "none" }} dangerouslySetInnerHTML={{ __html: printData }} />
      )}
    </Box>
  );
};

export default React.memo(EInvoice);